import { ChevronDown, Megaphone } from "lucide-react"

import { Eyebrow, Panel, SectionHead } from "./Panel"
import PriceCard from "./PriceCard"
import { formatDate, formatDollars } from "./format"

// ---- offer: how it works --------------------------------------------------

export function HowItWorks({ plan }) {
  const steps = [
    ["Subscribe", `${formatDollars(plan?.amountCents)}/month. Insider rates apply to every new order from that moment.`],
    ["Order normally", "Place jobs as you always do. The lower price is applied automatically, with no codes and no checkout step."],
    ["Stay one step behind", "When TUW raises standard prices, your rate moves to the previous standard price, so you always pay yesterday's rates."],
  ]
  return (
    <>
      <SectionHead title="How it works" />
      <ol className="grid grid-cols-3 gap-[28px] max-[861px]:grid-cols-1 max-[861px]:gap-[20px]">
        {steps.map(([title, body], i) => (
          <li key={title} className="px-[2px]">
            <div className="flex size-[30px] items-center justify-center rounded-full border-[1.5px] border-line-2 font-display text-[13px] font-bold">
              {i + 1}
            </div>
            <h3 className="mt-[14px] mb-[6px] font-display text-[15px] font-bold tracking-[-0.2px]">{title}</h3>
            <p className="text-[13px] leading-[1.6] text-muted-foreground">{body}</p>
          </li>
        ))}
      </ol>
    </>
  )
}

// ---- offer: before you subscribe ------------------------------------------

// Service groups that cost the same on both live price lists today, named in
// the FAQ so it never contradicts the breakdown table.
export function sameTodayGroups(comparison) {
  return (comparison?.groups ?? [])
    .filter((g) => g.items.length && g.items.every((i) => i.standard_cents != null && i.standard_cents === i.insider_cents))
    .map((g) => g.title)
}

function servicesAnswer(comparison) {
  const same = sameTodayGroups(comparison)
  const names = same.length > 1 ? `${same.slice(0, -1).join(", ")} and ${same.at(-1)}` : same[0]
  return `Every TUW service, including any not shown in the breakdown.${
    names ? ` ${names} currently ${same.length > 1 ? "cost" : "costs"} the same on both price lists.` : ""
  }`
}

function faqs(plan, comparison, closesOn) {
  return [
    ["Does it apply to orders I've already placed?", "No. Insider rates apply to new orders placed after you subscribe. Orders already in progress keep the price they were posted at."],
    ["Which services are included?", servicesAnswer(comparison)],
    ["What happens if I cancel?", "New orders go back to standard pricing, and you can't subscribe again. Insider is a one-time enrollment, so the lock can't be restored once it ends."],
    [
      "Can I join later?",
      closesOn
        ? `Only while enrollment is open. The current window closes on ${closesOn}.`
        : "Yes. Enrollment is open with no closing date for now. If that changes, the closing date will be shown on this page.",
    ],
    ["How am I billed?", `${formatDollars(plan?.amountCents)} a month through Stripe. Update your payment method, change billing details or cancel from the Stripe portal at any time.`],
  ]
}

export function BeforeYouSubscribe({ plan, comparison, endpoints, closesAt, closeRef }) {
  const closesOn = formatDate(closesAt)
  return (
    <div className="mt-[34px] grid grid-cols-[minmax(240px,1fr)_minmax(0,1.7fr)] items-start gap-[28px] max-[861px]:grid-cols-1 max-[861px]:gap-[18px]">
      <div ref={closeRef}>
        <h2 className="font-display text-[22px] font-bold tracking-[-0.5px] max-[561px]:text-[19px]">Before you subscribe</h2>
        <p className="mt-[8px] text-[13.5px] leading-[1.55] text-muted-foreground">
          The details that matter before you commit.{" "}
          {closesOn ? `Enrollment closes on ${closesOn}; your` : "Your"} Insider rates start with your next order.
        </p>
        <PriceCard plan={plan} endpoints={endpoints} fine="Cancel anytime, but you can't rejoin afterwards." className="mt-[20px]" />
      </div>
      <div className="border-y border-border">
        {faqs(plan, comparison, closesOn).map(([q, a]) => (
          <details key={q} className="group/faq border-t border-border first:border-t-0">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-[14px] px-[2px] py-[15px] text-[14px] font-semibold text-foreground hover:text-muted-foreground [&::-webkit-details-marker]:hidden">
              {q}
              <ChevronDown className="size-[15px] shrink-0 text-ink-3 transition-transform group-open/faq:rotate-180 motion-reduce:transition-none" strokeWidth={2} />
            </summary>
            <p className="max-w-[720px] px-[2px] pb-[16px] text-[13.5px] leading-[1.6] text-muted-foreground">{a}</p>
          </details>
        ))}
      </div>
    </div>
  )
}

// ---- member: announcement -------------------------------------------------

export function Announcement() {
  return (
    <Panel className="px-[22px] py-[20px] max-[861px]:px-[14px] max-[861px]:py-[16px]">
      <Eyebrow>
        <Megaphone strokeWidth={2.2} /> What&apos;s new
      </Eyebrow>
      <h2 className="mt-[8px] mb-[6px] font-display text-[16px] font-bold tracking-[-0.2px]">This is your Insider Hub.</h2>
      <p className="max-w-[680px] text-[13.5px] leading-[1.6] text-muted-foreground">
        Rate-rotation updates, member-only promos, and early access to new services land here. Check back when you log
        in. We&apos;ll keep it short.
      </p>
    </Panel>
  )
}
